import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { REC_TYPE_META } from '@/components/RecommendedForYou';

const SECTORS = ['Logistics', 'Transportation', 'Consulting', 'Manufacturing', 'Retail', 'Energy', 'Technology', 'Ports & Maritime', 'Construction', 'FMCG'];

const EMPTY = { item_type: 'course', item_id: '', title: '', description: '', url: '', sectors: [], backgrounds: [] };

const ChipPicker = ({ label, options, value, onChange, testid }) => (
  <div>
    <Label className="text-xs text-slate-500">{label} <span className="text-slate-400">(leave empty for everyone)</span></Label>
    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={testid}>
      {options.map(o => {
        const on = value.includes(o);
        return (
          <button key={o} type="button" data-testid={`${testid}-${o}`}
            onClick={() => onChange(on ? value.filter(v => v !== o) : [...value, o])}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
            {o}
          </button>
        );
      })}
    </div>
  </div>
);

const ItemSearch = ({ type, selected, onSelect }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => {
      fetch(`/api/admin/featured-recommendations/search?type=${type}&q=${encodeURIComponent(q)}`, { credentials: 'include' })
        .then(r => (r.ok ? r.json() : [])).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [type, q]);
  return (
    <div className="min-w-0">
      <Input value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${REC_TYPE_META[type].label.toLowerCase()}s…`} data-testid="featured-rec-search-input" />
      <div className="mt-2 max-h-48 overflow-y-auto space-y-1" data-testid="featured-rec-search-results">
        {results.map(r => (
          <button key={r.id} type="button" onClick={() => onSelect(r.id)} data-testid={`featured-rec-result-${r.id}`}
            className={`w-full text-left px-3 py-2 rounded-md border text-xs flex items-center gap-2 transition-colors ${selected === r.id ? 'border-brand-navy bg-brand-navy/5' : 'border-slate-100 hover:bg-slate-50'}`}>
            <span className="flex-1 min-w-0"><span className="font-medium text-slate-700 block truncate">{r.title}</span>
              <span className="text-slate-400 block truncate">{r.description}</span></span>
            {selected === r.id && <Check className="h-4 w-4 text-brand-navy flex-shrink-0" />}
          </button>
        ))}
        {results.length === 0 && <p className="text-xs text-slate-400 italic px-1">No matches</p>}
      </div>
    </div>
  );
};

export default function FeaturedRecForm({ options, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const isLink = form.item_type === 'link';

  const submit = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/admin/featured-recommendations', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, item_id: isLink ? null : form.item_id }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Could not add item');
      toast.success('Added to Recommended For You');
      setForm({ ...EMPTY, item_type: form.item_type });
      onCreated(d);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="featured-rec-form">
      <div className="grid sm:grid-cols-[200px_minmax(0,1fr)] gap-4">
        <div>
          <Label className="text-xs text-slate-500">Type</Label>
          <Select value={form.item_type} onValueChange={v => set({ item_type: v, item_id: '' })}>
            <SelectTrigger className="mt-1.5" data-testid="featured-rec-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(REC_TYPE_META).map(([k, m]) => (
                <SelectItem key={k} value={k} data-testid={`featured-rec-type-${k}`}>{k === 'link' ? 'Custom link' : m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isLink ? (
          <div className="grid gap-2">
            <Input value={form.title} onChange={e => set({ title: e.target.value })} placeholder="Title" data-testid="featured-rec-link-title" />
            <Input value={form.description} onChange={e => set({ description: e.target.value })} placeholder="Short description" data-testid="featured-rec-link-description" />
            <Input value={form.url} onChange={e => set({ url: e.target.value })} placeholder="https://… or /internal-page" data-testid="featured-rec-link-url" />
          </div>
        ) : (
          <ItemSearch type={form.item_type} selected={form.item_id} onSelect={id => set({ item_id: id })} />
        )}
      </div>
      <ChipPicker label="Audience: sectors" options={SECTORS} value={form.sectors} onChange={v => set({ sectors: v })} testid="featured-rec-sectors" />
      <ChipPicker label="Audience: stakeholder type" options={options.stakeholder_type || []} value={form.backgrounds} onChange={v => set({ backgrounds: v })} testid="featured-rec-backgrounds" />
      <Button onClick={submit} disabled={saving || (isLink ? !(form.title && form.url) : !form.item_id)}
        className="bg-brand-navy hover:bg-brand-dark text-white" data-testid="featured-rec-add-btn">
        <Plus className="h-4 w-4 mr-1.5" /> {saving ? 'Adding…' : 'Pin to Recommended For You'}
      </Button>
    </div>
  );
}
